import { Link } from "react-router-dom";

import { CardVisibility } from "@/api/types";
import { ComicPanel } from "@/components/comic";

/**
 * Who may see the agent card. Controlled by {@link ProfileForm} so the value rides the same PATCH
 * as the rest of the profile (one Save button).
 *
 * <p>A panel, not a section: it sits beside "Being reachable" under one "Agent requests" banner,
 * because both answer the same question — who may reach me through an agent — and reading them as
 * two unrelated sections was what made the card feel like a separate feature.</p>
 *
 * <p>The options are named and nothing more. Each carried a sentence explaining when to pick it
 * ("when you use booking links without a Team…"), which described a situation rather than the
 * setting and was longer than the choice it qualified.</p>
 */
export function AgentCardSettingsPanel({
  handle,
  visibility,
  disabled,
  onVisibilityChange,
}: {
  handle: string;
  visibility: CardVisibility;
  disabled: boolean;
  onVisibilityChange: (visibility: CardVisibility) => void;
}) {
  return (
    <ComicPanel title="Who can see your card" className="h-full">
      <fieldset className="space-y-2">
        <legend className="sr-only">Who can see your card</legend>
        {(
          [
            { value: "private", label: "Private" },
            { value: "discoverable", label: "Discoverable in the app" },
          ] as const
        ).map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-3 rounded-md border border-border bg-background px-3 py-2"
          >
            <input
              type="radio"
              name="cardVisibility"
              value={option.value}
              checked={visibility === option.value}
              disabled={disabled}
              onChange={() => onVisibilityChange(option.value)}
            />
            <span className="text-sm font-semibold">{option.label}</span>
          </label>
        ))}
      </fieldset>

      {/* The only route to your own card. It was the "card" in the removed "Your agent card"
          heading, and dropping it with the heading would leave no way to see the thing this
          panel governs. */}
      <Link
        to={`/@${handle}`}
        className="mt-2 inline-block text-xs text-brand-blue underline-offset-2 hover:underline"
      >
        View your card →
      </Link>
    </ComicPanel>
  );
}
